import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { Link } from 'react-router-dom';
import { ScrollArea } from 'components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import {
  Trophy, Star, Zap, Target, BookOpen,
  CheckCircle2, Clock, Medal, Crown, TrendingUp,
  Users, ChevronRight, Lock
} from 'lucide-react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, ResponsiveContainer,
} from 'recharts';

/* ─── COLORES DE MARCA ────────────────────────────────────────────────────────
   --c1 = primario · --c2 = secundario · --c3 = acento
   --neon = intensidad de los neones (1 = fuerte, 0.6 = más suave, 0 = sin neón)
──────────────────────────────────────────────────────────────────────────── */
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');

  @keyframes scanLine { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

  .pg-root {
    --neon: 1;
    --c1: var(--brand-primary, #00F5FF);
    --c2: var(--brand-secondary, #A855F7);
    --c3: var(--brand-accent, #CCFF00);
    --c1-rgb: var(--brand-primary-rgb, 0 245 255);
    --c2-rgb: var(--brand-secondary-rgb, 168 85 247);
    --c3-rgb: var(--brand-accent-rgb, 204 255 0);
  }

  .dash-root {
    font-family: var(--org-font, 'DM Sans', sans-serif);
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
    animation: fadeIn 0.4s ease;
  }

  .dash-root::before {
    content: ''; position: fixed; top: 0; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, transparent, rgb(var(--c2-rgb) / 0.6), transparent);
    animation: scanLine 8s linear infinite; z-index: 1; pointer-events: none;
  }

  .main-content { position: relative; z-index: 2; padding: 32px 40px; }

  /* Tarjetas oscuras: sube/baja los dos .6 para más o menos oscuridad */
  .glass-panel {
    background:
      linear-gradient(rgb(0 0 0 / .6), rgb(0 0 0 / .6)),
      rgb(var(--brand-bg-rgb, 6 5 20) / .9);
    backdrop-filter: blur(24px) saturate(160%);
    -webkit-backdrop-filter: blur(24px) saturate(160%);
    border: 1px solid rgb(var(--brand-text-rgb, 255 255 255) / 0.1);
    border-radius: 20px;
    position: relative;
    transition: all 0.3s ease;
    color: #fff;
  }

  .title-neon {
    color: var(--brand-text, #fff);
    text-shadow: 0 0 calc(18px * var(--neon)) rgb(var(--c2-rgb) / calc(0.7 * var(--neon)));
    letter-spacing: -0.02em;
  }
  .subtitle-brand {
    color: rgb(var(--brand-text-rgb, 255 255 255) / 0.8);
    font-size: 10px; text-transform: uppercase;
    letter-spacing: 0.2em; font-weight: 700; margin-top: 8px;
  }

  /* ── Neones (cada uno sigue un color de marca) ── */
  .glow-accent1 {
    box-shadow:
      0 0 calc(18px * var(--neon)) rgb(var(--c1-rgb) / calc(0.55 * var(--neon))),
      0 0 calc(40px * var(--neon)) rgb(var(--c1-rgb) / calc(0.22 * var(--neon))),
      inset 0 0 14px rgb(var(--c1-rgb) / calc(0.08 * var(--neon)));
    border-color: rgb(var(--c1-rgb) / calc(0.85 * var(--neon)));
  }
  .glow-accent2 {
    box-shadow:
      0 0 calc(18px * var(--neon)) rgb(var(--c2-rgb) / calc(0.55 * var(--neon))),
      0 0 calc(40px * var(--neon)) rgb(var(--c2-rgb) / calc(0.22 * var(--neon))),
      inset 0 0 14px rgb(var(--c2-rgb) / calc(0.08 * var(--neon)));
    border-color: rgb(var(--c2-rgb) / calc(0.85 * var(--neon)));
  }
  .glow-accent3 {
    box-shadow:
      0 0 calc(18px * var(--neon)) rgb(var(--c3-rgb) / calc(0.55 * var(--neon))),
      0 0 calc(40px * var(--neon)) rgb(var(--c3-rgb) / calc(0.22 * var(--neon))),
      inset 0 0 14px rgb(var(--c3-rgb) / calc(0.08 * var(--neon)));
    border-color: rgb(var(--c3-rgb) / calc(0.85 * var(--neon)));
  }
  /* Paneles grandes sin color propio: neón secundario */
  .glow-panel {
    box-shadow:
      0 0 calc(16px * var(--neon)) rgb(var(--c2-rgb) / calc(0.4 * var(--neon))),
      0 0 calc(36px * var(--neon)) rgb(var(--c2-rgb) / calc(0.16 * var(--neon)));
    border-color: rgb(var(--c2-rgb) / calc(0.7 * var(--neon)));
  }

  .level-track {
    background: rgb(255 255 255 / 0.1); border-radius: 4px; height: 6px;
    overflow: hidden; margin-top: 8px; box-shadow: inset 0 1px 3px rgba(0,0,0,0.3);
  }
  .level-fill {
    height: 100%; border-radius: 4px;
    background: linear-gradient(90deg, var(--c1), color-mix(in srgb, var(--c1) 70%, white));
    box-shadow: 0 0 12px var(--c1);
    transition: width 0.5s ease;
  }

  .section-label {
    display: flex; align-items: center; gap: 8px;
    font-size: 13px; font-weight: 800; color: #fff;
    text-transform: uppercase; letter-spacing: 0.1em;
  }

  /* ── Pestañas: inactivas con el color más claro, activa intensa ── */
  .pg-tabs-list {
    background: rgb(0 0 0 / 0.4);
    border: 1px solid rgb(var(--brand-text-rgb, 255 255 255) / 0.12);
  }
  .pg-tab {
    color: #fff !important;
    border: 1px solid transparent;
    transition: all 0.2s;
  }
  .pg-tab.tab-accent1 { background: rgb(var(--c1-rgb) / 0.14) !important; border-color: rgb(var(--c1-rgb) / 0.4) !important; }
  .pg-tab.tab-accent2 { background: rgb(var(--c2-rgb) / 0.14) !important; border-color: rgb(var(--c2-rgb) / 0.4) !important; }
  .pg-tab.tab-accent3 { background: rgb(var(--c3-rgb) / 0.14) !important; border-color: rgb(var(--c3-rgb) / 0.4) !important; }

  .pg-tab.tab-accent1:hover { background: rgb(var(--c1-rgb) / 0.28) !important; }
  .pg-tab.tab-accent2:hover { background: rgb(var(--c2-rgb) / 0.28) !important; }
  .pg-tab.tab-accent3:hover { background: rgb(var(--c3-rgb) / 0.28) !important; }

  [data-state=active].tab-accent1 {
    background: rgb(var(--c1-rgb) / 0.4) !important; border-color: var(--c1) !important;
    box-shadow: 0 0 calc(16px * var(--neon)) rgb(var(--c1-rgb) / calc(0.7 * var(--neon)));
  }
  [data-state=active].tab-accent2 {
    background: rgb(var(--c2-rgb) / 0.4) !important; border-color: var(--c2) !important;
    box-shadow: 0 0 calc(16px * var(--neon)) rgb(var(--c2-rgb) / calc(0.7 * var(--neon)));
  }
  [data-state=active].tab-accent3 {
    background: rgb(var(--c3-rgb) / 0.4) !important; border-color: var(--c3) !important;
    box-shadow: 0 0 calc(16px * var(--neon)) rgb(var(--c3-rgb) / calc(0.7 * var(--neon)));
  }
`;

const Progress = () => {
  const { user, api } = useAuth();

  const [stats, setStats] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [assignedModules, setAssignedModules] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [rankingMode, setRankingMode] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, achievementsRes, modulesRes, usersRes] = await Promise.all([
        api.get('/stats/dashboard'),
        api.get('/achievements'),
        api.get('/modules/assigned'),
        api.get('/users/ranking').catch(() => ({ data: { mode: 'all', users: [] } })),
      ]);

      const currentStats = statsRes.data;
      const rawRanking = usersRes.data?.users || [];
      const mode = usersRes.data?.mode || 'all';
      setRankingMode(mode);

      setStats(currentStats);
      setAchievements(achievementsRes.data || []);
      setAssignedModules(modulesRes.data || []);

      const activeUsers = rawRanking.filter(person => {
        const name = person.name?.toLowerCase() || '';
        return !name.includes('eliminado');
      });

      const userXp = currentStats?.total_xp ?? user?.xp ?? 0;
      const userInRanking = activeUsers.some(r => String(r.id) === String(user?.id));

      const mergedRanking = [...activeUsers];
      if (mode !== 'none' && !userInRanking && user?.id) {
        mergedRanking.push({ id: user.id, name: user.name, xp: userXp });
      }

      mergedRanking.sort((a, b) => (b.xp || 0) - (a.xp || 0));
      setRanking(mergedRanking.map((person, index) => ({ ...person, position: index + 1 })));
    } catch (error) {
      console.error('Error fetching progress data:', error);
    } finally {
      setLoading(false);
    }
  }, [api, user?.id, user?.name, user?.xp]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { completedModules, pendingModules, inProgressModules } = useMemo(() => ({
    completedModules: assignedModules.filter(m => m.assignment_status === 'completed'),
    pendingModules: assignedModules.filter(m => m.assignment_status === 'pending'),
    inProgressModules: assignedModules.filter(m => m.assignment_status === 'in_progress'),
  }), [assignedModules]);

  const skillsData = useMemo(() => {
    const skillsMap = {};
    assignedModules.forEach(m => {
      const weight = m.assignment_status === 'completed' ? 30 : m.assignment_status === 'in_progress' ? 15 : 10;
      const skills = (m.skills && m.skills.length > 0) ? m.skills : m.category ? [m.category] : [];
      skills.forEach(skill => {
        if (skill) skillsMap[skill] = (skillsMap[skill] || 0) + weight;
      });
    });
    return Object.entries(skillsMap)
      .map(([skill, value]) => ({ skill, value: Math.min(value, 100), fullMark: 100 }))
      .slice(0, 8);
  }, [assignedModules]);

  const totalXp = useMemo(() => stats?.total_xp ?? user?.xp ?? 0, [stats?.total_xp, user?.xp]);
  const level = useMemo(() => Math.floor(totalXp / 500) + 1, [totalXp]);
  const currentLevelXp = useMemo(() => totalXp % 500, [totalXp]);
  const levelProgress = useMemo(() => (currentLevelXp / 500) * 100, [currentLevelXp]);

  const myRankEntry = useMemo(() => ranking.find(r => String(r.id) === String(user?.id)), [ranking, user?.id]);
  const myPosition = myRankEntry?.position || '—';
  const totalInRanking = ranking.length;

  const getMedal = (pos) => {
    if (pos === 1) return { icon: Crown, color: 'text-yellow-400', bg: 'bg-yellow-400/20 border border-yellow-400/30', label: '1º' };
    if (pos === 2) return { icon: Medal, color: 'text-slate-300',  bg: 'bg-slate-300/20 border border-slate-300/30',  label: '2º' };
    if (pos === 3) return { icon: Medal, color: 'text-amber-600',  bg: 'bg-amber-600/20 border border-amber-600/30',  label: '3º' };
    return { icon: Users, color: 'text-white/50', bg: 'bg-white/5 border border-white/10', label: `${pos}º` };
  };

  const achievementIcons = { rocket: '🚀', zap: '⚡', star: '⭐', trophy: '🏆', medal: '🏅', fire: '🔥' };

  const allPossibleAchievements = [
    { title: 'Primeros Pasos',          description: 'Completa tu primer módulo', icon: 'rocket', xp_reward: 100,  threshold: 1  },
    { title: 'Aprendiz Rápido',         description: 'Completa 5 módulos',        icon: 'zap',    xp_reward: 250,  threshold: 5  },
    { title: 'Estudiante Dedicado',     description: 'Completa 10 módulos',       icon: 'star',   xp_reward: 500,  threshold: 10 },
    { title: 'Buscador de Conocimiento', description: 'Completa 25 módulos',      icon: 'trophy', xp_reward: 1000, threshold: 25 },
  ];

  const lockedAchievements = useMemo(() => {
    const unlockedIcons = achievements.map(a => a.icon);
    return allPossibleAchievements.filter(a => !unlockedIcons.includes(a.icon));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [achievements]);

  const rankingTitle = rankingMode === 'department' ? 'Ranking de mi departamento' : 'Ranking de la Empresa';
  const rankingStatLabel = rankingMode === 'department' ? 'Ranking Dpto.' : 'Ranking Empresa';

  if (loading) {
    return (
      <div className="pg-root dash-root flex items-center justify-center" style={{ color: 'var(--brand-text, #fff)' }}>
        <style>{GLOBAL_STYLES}</style>
        <div className="flex flex-col items-center gap-4">
          <div
            className="w-12 h-12 border-4 rounded-full animate-spin"
            style={{ borderColor: 'var(--c1)', borderTopColor: 'transparent', boxShadow: '0 0 15px var(--c1)' }}
          />
          <span className="text-sm font-bold tracking-widest" style={{ color: 'var(--c1)' }}>CARGANDO DATOS...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="pg-root cyber-font min-h-screen p-8 relative">
      <style>{GLOBAL_STYLES}</style>

      <div className="main-content space-y-8">
        {/* HEADER */}
        <div>
          <h1 className="text-4xl font-black title-neon" style={{ fontFamily: 'Roberta, serif' }}>
            MI PROGRESO
          </h1>
          <p className="subtitle-brand">Revisa tu evolución y logros reales</p>
        </div>

        {/* STATS TOP GRID */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {/* Nivel */}
          <div className="glass-panel p-6 glow-accent1 flex flex-col justify-between">
            <div className="flex items-start gap-4 mb-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: 'rgb(var(--c1-rgb) / 0.2)', border: '1px solid rgb(var(--c1-rgb) / 0.5)' }}
              >
                <Star className="w-6 h-6" style={{ color: 'var(--c1)' }} />
              </div>
              <div>
                <p className="text-[10px] font-black text-white/60 tracking-widest uppercase">Nivel</p>
                <p className="text-4xl font-black text-white leading-none mt-1">{level}</p>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-[11px] font-bold text-white/60">
                <span>Siguiente nivel</span>
                <span style={{ color: 'var(--c1)' }}>{Math.round(levelProgress)}%</span>
              </div>
              <div className="level-track">
                <div className="level-fill" style={{ width: `${levelProgress}%` }} />
              </div>
              <div className="text-right text-[10px] text-white/40 font-bold mt-2 uppercase tracking-wider">
                {currentLevelXp} / 500 XP
              </div>
            </div>
          </div>

          {/* XP Total */}
          <div className="glass-panel p-6 glow-accent3 flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgb(var(--c3-rgb) / 0.2)', border: '1px solid rgb(var(--c3-rgb) / 0.5)' }}
            >
              <Zap className="w-6 h-6" style={{ color: 'var(--c3)', filter: 'drop-shadow(0 0 8px var(--c3))' }} />
            </div>
            <div>
              <p className="text-[10px] font-black text-white/60 tracking-widest uppercase">XP Total</p>
              <p className="text-4xl font-black text-white leading-none mt-1">{totalXp}</p>
            </div>
          </div>

          {/* Completados */}
          <div className="glass-panel p-6 glow-accent2 flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgb(var(--c2-rgb) / 0.2)', border: '1px solid rgb(var(--c2-rgb) / 0.5)' }}
            >
              <CheckCircle2 className="w-6 h-6" style={{ color: 'var(--c2)', filter: 'drop-shadow(0 0 8px var(--c2))' }} />
            </div>
            <div>
              <p className="text-[10px] font-black text-white/60 tracking-widest uppercase">Completados</p>
              <p className="text-4xl font-black text-white leading-none mt-1">
                {completedModules.length}<span className="text-xl text-white/40 font-bold">/{assignedModules.length}</span>
              </p>
            </div>
          </div>

          {/* Ranking */}
          <div className="glass-panel p-6 glow-accent1 flex items-center gap-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgb(var(--c1-rgb) / 0.2)', border: '1px solid rgb(var(--c1-rgb) / 0.5)' }}
            >
              <Trophy className="w-6 h-6" style={{ color: 'var(--c1)', filter: 'drop-shadow(0 0 8px var(--c1))' }} />
            </div>
            <div>
              <p className="text-[10px] font-black text-white/60 tracking-widest uppercase">{rankingStatLabel}</p>
              <p className="text-4xl font-black text-white leading-none mt-1">
                {rankingMode === 'none' ? '—' : (
                  <>{myPosition}<span className="text-xl text-white/40 font-bold">/{totalInRanking}</span></>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* MIDDLE GRID */}
        <div className="grid lg:grid-cols-2 gap-5">
          {/* Radar */}
          <div className="glass-panel glow-panel p-6 flex flex-col min-h-[360px]">
            <div className="section-label mb-6">
              <Target className="w-5 h-5" style={{ color: 'var(--c2)' }} /> Radar de Habilidades
            </div>
            <div className="flex-1 w-full">
              {skillsData.length >= 3 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart data={skillsData} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                    <PolarGrid stroke="rgba(255,255,255,0.18)" />
                    <PolarAngleAxis dataKey="skill" tick={{ fill: 'rgba(255,255,255,0.8)', fontSize: 11, fontWeight: 600 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: 'rgba(255,255,255,0.35)' }} axisLine={false} />
                    <Radar
                      name="Habilidades"
                      dataKey="value"
                      stroke="var(--brand-secondary, #A855F7)"
                      strokeWidth={2}
                      fill="var(--brand-secondary, #A855F7)"
                      fillOpacity={0.4}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              ) : skillsData.length > 0 ? (
                <div className="space-y-5 pt-4">
                  {skillsData.map(s => (
                    <div key={s.skill}>
                      <div className="flex justify-between text-[12px] font-bold text-white/80 mb-2">
                        <span>{s.skill}</span>
                        <span style={{ color: 'var(--c2)' }}>{s.value}%</span>
                      </div>
                      <div className="level-track" style={{ marginTop: 0 }}>
                        <div
                          className="level-fill"
                          style={{ width: `${s.value}%`, background: 'var(--c2)', boxShadow: '0 0 12px var(--c2)' }}
                        />
                      </div>
                    </div>
                  ))}
                  <p className="text-[10px] text-white/40 uppercase tracking-widest pt-2">
                    El radar aparece cuando tengas 3 o más habilidades
                  </p>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center text-white/40 flex-col gap-3">
                  <Target className="w-12 h-12 opacity-30" />
                  <p className="text-sm font-medium">Completa módulos para ver tus habilidades</p>
                </div>
              )}
            </div>
          </div>

          {/* Ranking List */}
          <div className="glass-panel glow-panel p-6 flex flex-col min-h-[360px]">
            <div className="flex justify-between items-center mb-6">
              <div className="section-label">
                <Crown className="w-5 h-5 text-yellow-400" /> {rankingTitle}
              </div>
              {rankingMode !== 'none' && (
                <span
                  className="text-[9px] font-black px-3 py-1.5 rounded-md tracking-widest uppercase"
                  style={{ background: 'rgb(var(--c3-rgb) / 0.2)', color: 'var(--c3)', border: '1px solid rgb(var(--c3-rgb) / 0.5)' }}
                >
                  {totalInRanking} Participantes
                </span>
              )}
            </div>

            <ScrollArea className="flex-1 pr-4 -mr-4">
              {ranking.length > 0 ? (
                <div className="space-y-3">
                  {ranking.map((person) => {
                    const isMe = String(person.id) === String(user?.id);
                    const medal = getMedal(person.position);
                    const MedalIcon = medal.icon;
                    return (
                      <div
                        key={person.id}
                        className={`flex items-center gap-4 px-4 py-3 rounded-xl transition-all border ${isMe ? '' : 'bg-white/[0.03] border-transparent hover:bg-white/[0.07] hover:border-white/15'}`}
                        style={isMe ? {
                          background: 'rgb(var(--c1-rgb) / 0.14)',
                          border: '1px solid rgb(var(--c1-rgb) / 0.6)',
                          boxShadow: '0 0 16px rgb(var(--c1-rgb) / 0.35)',
                        } : {}}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${medal.bg}`}>
                          <MedalIcon className={`w-5 h-5 ${medal.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[15px] font-bold truncate" style={{ color: isMe ? 'var(--c1)' : 'rgb(255 255 255 / 0.9)' }}>
                            {person.name}{' '}
                            {isMe && (
                              <span
                                className="text-[10px] px-1.5 py-0.5 rounded uppercase ml-2 tracking-widest font-black"
                                style={{ background: 'var(--c1)', color: '#000' }}
                              >
                                Tú
                              </span>
                            )}
                          </p>
                          <p className="text-[10px] text-white/50 font-black uppercase tracking-wider mt-0.5">{medal.label} Puesto</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-base font-black tracking-wide" style={{ color: isMe ? 'var(--c1)' : 'var(--c3)' }}>
                            {person.xp || 0} XP
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-white/40 flex-col gap-3">
                  <Users className="w-12 h-12 opacity-30" />
                  <p className="text-sm">
                    {rankingMode === 'none'
                      ? 'Tu empresa ha desactivado el ranking'
                      : 'No hay datos de ranking disponibles'}
                  </p>
                </div>
              )}
            </ScrollArea>
          </div>
        </div>

        {/* BOTTOM GRID */}
        <div className="grid grid-cols-1 gap-5">
          {/* Logros */}
          <div className="glass-panel glow-panel p-6">
            <div className="flex justify-between items-center mb-6">
              <div className="section-label">
                <Trophy className="w-5 h-5" style={{ color: 'var(--c3)' }} /> Logros
              </div>
              <span
                className="text-[9px] font-black px-3 py-1.5 rounded-md tracking-widest uppercase"
                style={{ background: 'rgb(var(--c3-rgb) / 0.2)', color: 'var(--c3)', border: '1px solid rgb(var(--c3-rgb) / 0.5)' }}
              >
                {achievements.length} Desbloqueados
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {achievements.map(achievement => (
                <div
                  key={achievement.id}
                  className="p-5 rounded-xl flex flex-col items-center text-center gap-2 transition-transform hover:-translate-y-1"
                  style={{
                    background: 'rgb(var(--c1-rgb) / 0.12)',
                    border: '1px solid rgb(var(--c1-rgb) / 0.55)',
                    boxShadow: '0 0 calc(18px * var(--neon)) rgb(var(--c1-rgb) / calc(0.35 * var(--neon)))',
                  }}
                >
                  <div className="text-4xl drop-shadow-md mb-1">{achievementIcons[achievement.icon] || '🏆'}</div>
                  <h4 className="font-black text-[13px] text-white tracking-wide">{achievement.title}</h4>
                  <p className="text-[11px] text-white/70 leading-tight">{achievement.description}</p>
                  <span
                    className="mt-2 text-[10px] px-2 py-1 rounded font-black tracking-widest"
                    style={{ background: 'rgb(var(--c1-rgb) / 0.25)', color: 'var(--c1)', border: '1px solid rgb(var(--c1-rgb) / 0.55)' }}
                  >
                    +{achievement.xp_reward} XP
                  </span>
                </div>
              ))}

              {lockedAchievements.map(achievement => (
                <div key={achievement.title} className="p-5 rounded-xl flex flex-col items-center text-center gap-2 opacity-60 hover:opacity-90 transition-opacity bg-white/[0.03] border border-white/10">
                  <div className="text-4xl grayscale opacity-50 mb-1">{achievementIcons[achievement.icon] || '🏆'}</div>
                  <h4 className="font-black text-[13px] text-white/70 flex items-center gap-1.5 tracking-wide">
                    <Lock className="w-3.5 h-3.5" /> {achievement.title}
                  </h4>
                  <p className="text-[11px] text-white/50 leading-tight">{achievement.description}</p>
                  <span className="mt-2 bg-white/5 text-white/40 border border-white/10 text-[10px] px-2 py-1 rounded font-black tracking-widest">
                    +{achievement.xp_reward} XP
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Mis Módulos */}
          <div className="glass-panel glow-panel p-6">
            <div className="section-label mb-6">
              <BookOpen className="w-5 h-5" style={{ color: 'var(--c2)' }} /> Mis Módulos
            </div>

            <Tabs defaultValue="pending">
              <TabsList className="pg-tabs-list mb-6 p-1 rounded-lg inline-flex gap-1">
                <TabsTrigger value="pending" className="pg-tab tab-accent2 font-bold text-[11px] uppercase tracking-widest px-6 py-2.5 rounded-md transition-all">
                  Pendientes <span className="ml-1.5 opacity-70">({pendingModules.length})</span>
                </TabsTrigger>
                <TabsTrigger value="inprogress" className="pg-tab tab-accent1 font-bold text-[11px] uppercase tracking-widest px-6 py-2.5 rounded-md transition-all">
                  En Progreso <span className="ml-1.5 opacity-70">({inProgressModules.length})</span>
                </TabsTrigger>
                <TabsTrigger value="completed" className="pg-tab tab-accent3 font-bold text-[11px] uppercase tracking-widest px-6 py-2.5 rounded-md transition-all">
                  Completados <span className="ml-1.5 opacity-70">({completedModules.length})</span>
                </TabsTrigger>
              </TabsList>

              {['pending', 'inprogress', 'completed'].map(tab => {
                const list = tab === 'pending' ? pendingModules : tab === 'inprogress' ? inProgressModules : completedModules;
                const accentVar = tab === 'completed' ? 'var(--c3)' : tab === 'inprogress' ? 'var(--c1)' : 'var(--c2)';

                return (
                  <TabsContent key={tab} value={tab} className="mt-0 outline-none">
                    <ScrollArea className="h-[260px] pr-4 -mr-4">
                      {list.length > 0 ? (
                        <div className="space-y-3">
                          {list.map(module => (
                            <Link
                              key={module.id}
                              to={`/module/${module.id}`}
                              className="flex items-center justify-between p-4 bg-white/[0.03] border border-white/10 hover:bg-white/[0.08] hover:border-white/25 rounded-xl group transition-all"
                            >
                              <div className="flex items-center gap-4 flex-1 min-w-0">
                                <div
                                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                                  style={{
                                    background: `color-mix(in srgb, ${accentVar} 20%, transparent)`,
                                    border: `1px solid color-mix(in srgb, ${accentVar} 50%, transparent)`,
                                  }}
                                >
                                  {tab === 'completed' ? (
                                    <CheckCircle2 className="w-6 h-6" style={{ color: accentVar }} />
                                  ) : tab === 'inprogress' ? (
                                    <TrendingUp className="w-6 h-6" style={{ color: accentVar }} />
                                  ) : (
                                    <Clock className="w-6 h-6" style={{ color: accentVar }} />
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[15px] font-bold text-white truncate">
                                    {module.title}
                                  </p>
                                  <div className="flex items-center gap-3 mt-1.5">
                                    <span className="bg-white/10 text-white/70 border border-white/15 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest">
                                      {module.category}
                                    </span>
                                    {module.due_date && (
                                      <span className="text-[10px] text-orange-400 font-bold uppercase tracking-wider flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        Entrega: {new Date(module.due_date).toLocaleDateString('es-ES')}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="pl-4">
                                <ChevronRight className="w-5 h-5 text-white/30 flex-shrink-0 transition-colors group-hover:text-white/70" />
                              </div>
                            </Link>
                          ))}
                        </div>
                      ) : (
                        <div className="h-[200px] flex items-center justify-center text-white/40 border border-dashed border-white/15 rounded-xl">
                          <p className="text-sm font-medium tracking-wide">No hay módulos en esta categoría</p>
                        </div>
                      )}
                    </ScrollArea>
                  </TabsContent>
                );
              })}
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Progress;